/**
 * The frame side: accept the host's messages, and only the host's.
 */

import {
  PROTOCOL_VERSION, isChatView, isDetailOpens, isHostMessage, isReplyCost,
  type ChatView, type DetailOpens, type ReplyCost, type FrameMessage, type HostMessage, type PickFolder, type StartWork,
} from './protocol.ts';

/** A string with something in it. */
const nonEmpty = (v: unknown): v is string => typeof v === 'string' && v.trim() !== '';

/** A `start-work` `code` as the frame will use it, or nothing where it is not one. */
export function codeOf(v: unknown): NonNullable<StartWork['code']> | undefined {
  if (!v || typeof v !== 'object') return undefined;
  const { deviceId, path, readOnly } = v as { deviceId?: unknown; path?: unknown; readOnly?: unknown };
  if (!nonEmpty(deviceId) || !nonEmpty(path)) return undefined;
  // A read-only that is not a boolean is not a code: read as "no", a host that sent `'true'`
  // would get a run able to change the folder it asked to have only read.
  if (readOnly !== undefined && typeof readOnly !== 'boolean') return undefined;
  return { deviceId: deviceId.trim(), path, ...(readOnly === true ? { readOnly: true } : {}) };
}

/** The host's name for who answers, bounded, with a mark: its first letter where none is given. */
export function assistantOf(v: unknown): { name: string; monogram: string } | undefined {
  if (!v || typeof v !== 'object') return undefined;
  const { name, monogram } = v as { name?: unknown; monogram?: unknown };
  if (!nonEmpty(name) || name.trim().length > 40) return undefined;
  const mark = nonEmpty(monogram) && monogram.trim().length <= 2 ? monogram.trim() : [...name.trim()][0]!.toUpperCase();
  return { name: name.trim(), monogram: mark };
}

export interface FrameBridgeOptions {
  /**
   * Origins permitted to embed this frame.
   *
   * Required, and deliberately not defaulted to anything permissive. A bridge
   * that accepted any origin would let any page that framed Chat42 hand it a
   * token and drive it.
   */
  allowedOrigins: string[];
  onInit: (init: {
    token: string; theme?: Record<string, string>; surfaces?: Record<string, boolean>; view?: ChatView;
    assistant?: { name: string; monogram: string }; replyCost?: ReplyCost;
    /** The host answers `person-action` (ADR 0147). */
    personActions?: true;
  }) => void;
  /** The host's answer to a `person-action` this frame sent (ADR 0147). Only where `features` names it. */
  onPersonActionResult?: (result: { requestId: string; ok: boolean; said: string }) => void;
  /** The host's mode for the open session (ADR 0147). Only where `features` names `permission-mode`. */
  onSetPermissionMode?: (mode: 'manual' | 'auto') => void;
  /**
   * The host wants a session it stored the id of. `more` is what to make it with if the id is
   * gone; see `OpenSession`. The id stays the first argument, so an older handler still works.
   */
  onOpenSession?: (sessionId: string, more?: { model?: string; origin?: string; readOnly?: true }) => void;
  /**
   * The host wants a session made, or found by its `origin`. The title stays the first argument
   * so a handler written before the model and the origin were sent goes on working unchanged.
   */
  onNewSession?: (title?: string, more?: { model?: string; origin?: string; replace?: string }) => void;
  /** The host wants the open session on another model, without a turn. See `HostMessage`. */
  onSetModel?: (model: string) => void;
  /** Where the person is in the host's page now, replacing where they were. See `HostMessage`. */
  onLocation?: (context: Record<string, string>) => void;
  onEntryContext?: (context: Record<string, string>) => void;
  onTheme?: (theme: Record<string, string>) => void;
  /** The host wants the conversation drawn another way (ADR 0142). Only a view there is reaches it. */
  onSetView?: (view: ChatView) => void;
  /** The host wants each reply's cost shown another way (ADR 0145). Only a value there is reaches it. */
  onSetReplyCost?: (replyCost: ReplyCost) => void;
  /** What a reply's "Show the detail" does, as the host says (ADR 0146). Only a value there is reaches it. */
  onSetDetail?: (opens: DetailOpens) => void;
  /** What the person's next message is about, as the host says it, or null for none (Rule42 Plan 16, C2). */
  onSetFocus?: (focus: { title: string } | null) => void;
  /**
   * The host wants work started in the session it is showing. See `StartWork`. `code`, where it
   * is passed on, has a machine and a path that are strings with something in them; a `code`
   * that is not one is refused here with `chat42:error`, never passed on without its code.
   */
  onStartWork?: (work: StartWork) => void;
  /**
   * The host wants the person to choose a folder on a paired machine. Answer with `emit`, once,
   * with the same `requestId`. Only called where `features` names `pick-folder`.
   */
  onPickFolder?: (request: Omit<PickFolder, 'type'>) => void;
  /**
   * What this frame can do beyond the core messages, announced in `chat42:ready` (see
   * `FRAME_FEATURES`). A frame names only what it has wired, so a host never asks a frame for a
   * picker it does not draw.
   */
  features?: string[];
  onToken?: (token: string) => void;
}

export class Chat42FrameBridge {
  #hostOrigin?: string;
  #listener?: (e: MessageEvent) => void;

  constructor(private opts: FrameBridgeOptions) {
    if (!opts.allowedOrigins.length) {
      throw new Error(
        'A frame bridge needs an explicit allowedOrigins list. Accepting any origin ' +
          'would let any page that framed Chat42 hand it a token and drive it.',
      );
    }
  }

  start(): void {
    this.#listener = (event: MessageEvent) => {
      if (!this.opts.allowedOrigins.includes(event.origin)) return;
      if (!isHostMessage(event.data)) return;

      // The first accepted origin becomes *the* host for this frame's lifetime.
      // Without this, a second embedder could talk to a frame someone else
      // already initialised.
      this.#hostOrigin ??= event.origin;
      if (event.origin !== this.#hostOrigin) return;

      this.#handle(event.data);
    };

    window.addEventListener('message', this.#listener);
    // Announced to the opener rather than to a stored origin, because at this
    // point no host has identified itself yet.
    // Its features too, which are names of what it can do and nothing of the person's: safe to
    // say before anybody has said who they are.
    const features = this.opts.features?.filter(nonEmpty);
    window.parent?.postMessage({
      type: 'chat42:ready', version: PROTOCOL_VERSION, ...(features?.length ? { features } : {}),
    }, '*');
  }

  #handle(message: HostMessage): void {
    switch (message.type) {
      case 'chat42:init':
        if (message.version !== PROTOCOL_VERSION) {
          this.emit({
            type: 'chat42:error',
            message: `Host speaks protocol ${message.version}; this build speaks ${PROTOCOL_VERSION}.`,
          });
          return;
        }
        // A view that is not one is dropped, and the frame draws its default: never a guess.
        this.opts.onInit({
          token: message.token, theme: message.theme, surfaces: message.surfaces,
          ...(isChatView(message.view) ? { view: message.view } : {}),
          ...(assistantOf(message.assistant) ? { assistant: assistantOf(message.assistant)! } : {}),
          ...(isReplyCost(message.replyCost) ? { replyCost: message.replyCost } : {}),
          ...(message.personActions === true ? { personActions: true as const } : {}),
        });
        break;
      case 'chat42:open-session': {
        // Strings only, as for new-session below.
        // A read-only open takes nothing to make a session with: it never makes one (ADR 0135).
        // Only `true` counts, so a host that sent anything else opens as it always has.
        const more = message.readOnly === true ? { readOnly: true as const } : {
          ...(nonEmpty(message.model) ? { model: message.model } : {}),
          ...(nonEmpty(message.origin) ? { origin: message.origin } : {}),
        };
        this.opts.onOpenSession?.(message.sessionId, more);
        break;
      }
      case 'chat42:new-session': {
        // Only strings go on. A host's message is checked for its type and nothing else, and a
        // model or an origin that is not a string would reach a request body as whatever it was.
        const more = {
          ...(nonEmpty(message.model) ? { model: message.model } : {}),
          ...(nonEmpty(message.origin) ? { origin: message.origin } : {}),
          // Only with an origin: Start over keeps the scope's one conversation under its name.
          ...(nonEmpty(message.origin) && nonEmpty(message.replace) ? { replace: message.replace } : {}),
        };
        this.opts.onNewSession?.(message.title, more);
        break;
      }
      case 'chat42:set-model':
        // A message with no model is not a request to clear it: clearing would hand the choice
        // back to whatever default is below it, which is the silent choice this message exists
        // to stop.
        if (nonEmpty(message.model)) this.opts.onSetModel?.(message.model);
        break;
      case 'chat42:location': {
        // Strings only, for the reason new-session's fields are: the shape is checked by type
        // and nothing else. An empty one is passed on, because it says the person is nowhere in
        // particular and the old place should go.
        const context = message.context;
        if (!context || typeof context !== 'object' || Array.isArray(context)) break;
        const kept: Record<string, string> = {};
        for (const [key, value] of Object.entries(context)) if (typeof value === 'string') kept[key] = value;
        this.opts.onLocation?.(kept);
        break;
      }
      case 'chat42:entry-context': this.opts.onEntryContext?.(message.context); break;
      case 'chat42:set-theme': this.opts.onTheme?.(message.theme); break;
      case 'chat42:set-view':
        if (isChatView(message.view)) this.opts.onSetView?.(message.view);
        break;
      case 'chat42:set-reply-cost':
        if (isReplyCost(message.replyCost)) this.opts.onSetReplyCost?.(message.replyCost);
        break;
      case 'chat42:set-detail':
        if (isDetailOpens(message.opens)) this.opts.onSetDetail?.(message.opens);
        break;
      case 'chat42:set-focus': {
        // A title with something in it, kept to one line, or nothing: anything else is dropped unread.
        const raw = message.focus;
        if (raw === null) { this.opts.onSetFocus?.(null); break; }
        const title = raw && typeof raw === 'object' && typeof raw.title === 'string' ? raw.title.replace(/\s+/g, ' ').trim().slice(0, 160) : '';
        if (title) this.opts.onSetFocus?.({ title });
        break;
      }
      case 'chat42:token':
        // A renewal with nothing in it is not one: handed on, it would replace a working token
        // with an empty one and sign the frame out (ADR 0101).
        if (nonEmpty(message.token)) this.opts.onToken?.(message.token);
        break;
      case 'chat42:start-work': {
        // The code is checked here because a run about some code must never begin without it:
        // a `code` that is not a machine and a path is refused, not dropped. Dropped, the prompt
        // would run on no code at all and review nothing while saying it had.
        // A label that is not words is dropped: the view then says work was started from the page.
        const { label, ...rest } = message;
        const work: StartWork = nonEmpty(label) ? { ...rest, label: label.trim() } : rest;
        if (work.code === undefined) { this.opts.onStartWork?.(work); break; }
        const code = codeOf(work.code);
        if (!code) {
          this.emit({
            type: 'chat42:error',
            message: 'The run was not started: the folder to run on was not a machine and a path.',
            case: 'other',
          });
          break;
        }
        this.opts.onStartWork?.({ ...work, code });
        break;
      }
      case 'chat42:person-action-result':
        // An answer to nothing asked is dropped by its handler; one that is not whole is dropped here.
        if (!nonEmpty(message.requestId) || typeof message.ok !== 'boolean') break;
        if (!this.opts.features?.includes('person-action')) break;
        this.opts.onPersonActionResult?.({ requestId: message.requestId, ok: message.ok, said: typeof message.said === 'string' ? message.said.slice(0, 400) : '' });
        break;
      case 'chat42:set-permission-mode':
        // Only the two a host's setting maps to. Anything else, Bypass among them, is never a host's to set.
        if (message.mode !== 'manual' && message.mode !== 'auto') break;
        if (!this.opts.features?.includes('permission-mode')) break;
        this.opts.onSetPermissionMode?.(message.mode);
        break;
      case 'chat42:pick-folder': {
        // An id to answer with, or nothing to answer: a request without one cannot be told apart
        // from the next, and an answer to it would reach whichever of the host's waits is first.
        if (!nonEmpty(message.requestId)) break;
        if (!this.opts.features?.includes('pick-folder') || !this.opts.onPickFolder) break;
        const raw = message.hint;
        const hint: NonNullable<PickFolder['hint']> = {};
        if (raw && typeof raw === 'object') {
          if (nonEmpty(raw.repository)) hint.repository = raw.repository.trim();
          if (nonEmpty(raw.label)) hint.label = raw.label.trim();
        }
        this.opts.onPickFolder({ requestId: message.requestId, ...(Object.keys(hint).length ? { hint } : {}) });
        break;
      }
    }
  }

  /** Notifications only. Conversation content never crosses this boundary. */
  emit(message: FrameMessage): void {
    if (!this.#hostOrigin) return;
    window.parent?.postMessage(message, this.#hostOrigin);
  }

  stop(): void {
    if (this.#listener) window.removeEventListener('message', this.#listener);
    this.#listener = undefined;
    this.#hostOrigin = undefined;
  }
}
